<template>
  <v-card class="import-wizard">
    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center py-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
        class="mb-4"
      />
      <h3 class="text-h6">
        {{ $t('admin.productsDatabase.importWizard.loading') }}
      </h3>
    </div>
    
    <!-- Wizard Content -->
    <div v-else>
      <v-card-title class="d-flex justify-space-between align-center primary">
        <span class="text-h5 white--text">{{
          $t(`admin.productsDatabase.importWizard.steps.${currentStep}.title`)
        }}</span>
        <v-btn
          icon
          class="white--text"
          @click="confirmCloseWizard"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <ekitabu-stepper
        v-model="currentStepIndex"
        :steps="stepperSteps"
        :can-navigate="canNavigateToStep"
        flat
        @navigate="navigateToStep"
      >
        <template #step-1>
          <ProductUpload
            :product-info="productInfo"
            :product-info-manager="productInfoManager"
            @valid="handleStepValidation(STEPS.UPLOAD, $event)"
          />
          
          <StepperNavigation
            :show-back="false"
            :show-cancel="true"
            :next-disabled="!stepValidations[STEPS.UPLOAD]"
            @cancel="confirmCloseWizard"
            @next="currentStepIndex = 2"
          />
        </template>

        <template #step-2>
          <CoverArtUpload
            v-if="bookId || true"
            :key="'cover-art-' + (coverArtUrl || 'none')"
            :book-id="bookId || 'debug-id'"
            :initial-cover-url="coverArtUrl"
            :product-info-manager="productInfoManager"
            @upload-error="handleUploadError"
            @valid="handleStepValidation(STEPS.COVER_ART, $event)"
          />

          <v-alert
            v-else-if="currentStepIndex === 2"
            type="warning"
            outlined
            class="mb-4"
          >
            {{ $t('admin.productsDatabase.importWizard.steps.coverArt.bookIdRequired') }}
          </v-alert>
          
          <StepperNavigation
            :next-disabled="!stepValidations[STEPS.COVER_ART]"
            @back="currentStepIndex = 1"
            @next="currentStepIndex = 3"
          />
        </template>

        <template #step-3>
          <ProductDetails
            :product-info-manager="productInfoManager"
            @valid="handleStepValidation(STEPS.PRODUCT_DETAILS, $event)"
          />
          
          <StepperNavigation
            :next-disabled="!stepValidations[STEPS.PRODUCT_DETAILS]"
            @back="currentStepIndex = 2"
            @next="currentStepIndex = 4"
          />
        </template>

        <template #step-4>
          <PublicationDetails
            v-if="productInfoManager"
            :product-info-manager="productInfoManager"
            @valid="handleStepValidation(STEPS.PUBLICATION_DETAILS, $event)"
          />
          
          <StepperNavigation
            :next-disabled="!stepValidations[STEPS.PUBLICATION_DETAILS]"
            @back="currentStepIndex = 3"
            @next="currentStepIndex = 5"
          />
        </template>

        <template #step-5>
          <Price 
            v-if="productInfoManager"
            :product-info-manager="productInfoManager"
            @valid="handleStepValidation(STEPS.PRICE, $event)"
          />
          
          <StepperNavigation
            :next-disabled="!stepValidations[STEPS.PRICE]"
            @back="currentStepIndex = 4"
            @next="currentStepIndex = 6"
          />
        </template>

        <template #step-6>
          <ProductView 
            v-if="productInfo && productInfo.book"
            :book="productInfo.book"
            hide-edit-button
            hide-close-button
          />
          
          <StepperNavigation
            :next-disabled="!stepValidations[STEPS.REVIEW]"
            @back="currentStepIndex = 5"
            @next="currentStepIndex = 7"
          />
        </template>

        <template #step-7>
          <div class="release-step">
            <h3 class="text-h5 mb-2">
              {{ $t('admin.productsDatabase.importWizard.steps.release.title') }}
            </h3>
            <p class="text-body-1 mb-5">
              {{ $t('admin.productsDatabase.importWizard.steps.release.description') }}
            </p>
            
            <LocalBookReleaseScheduler
              v-if="bookId"
              :product-info-manager="productInfoManager"
              field-name="availableFrom"
              :save-immediately="false"
              @valid="handleStepValidation(STEPS.RELEASE, $event)"
              @release-type-changed="handleReleaseTypeChange"
            />
          </div>
          
          <v-divider class="mt-4 mb-4" />
          
          <div class="d-flex justify-end">
            <v-btn
              text
              @click="currentStepIndex = 6"
            >
              {{ $t('common.back') }}
            </v-btn>
            
            <PublishButton
              class="ml-2"
              :disabled="!stepValidations[STEPS.RELEASE]"
              :loading="isPublishing"
              :release-type="currentReleaseType"
              :has-pending-changes="productInfoManager?.unsavedFields?.size > 0"
              :has-release-changes="productInfoManager?.hasPendingReleaseChanges()"
              @click="confirmAndPublish"
            />
          </div>
        </template>

        <template #step-8>
          <div class="text-center py-8">
            <v-icon
              size="80"
              color="success"
              class="mb-4"
            >
              mdi-check-circle
            </v-icon>
            <h2 class="text-h4 mb-4">
              {{ $t('admin.productsDatabase.importWizard.changesPublishedSuccessfully') }}
            </h2>
            <p class="text-body-1 mb-4">
              {{ $t('admin.productsDatabase.importWizard.releaseScheduleUpdatedSuccessfully') }}
            </p>
            <v-btn
              color="primary"
              @click="finishWizard"
            >
              {{ $t('common.done') }}
            </v-btn>
          </div>
        </template>
      </ekitabu-stepper>

      <!-- Confirm close dialog -->
      <v-dialog
        v-model="showConfirmCloseDialog"
        max-width="400"
      >
        <v-card>
          <v-card-title>
            {{
              $t("admin.productsDatabase.importWizard.confirmCloseTitle")
            }}
          </v-card-title>
          <v-card-text>
            {{ $t("admin.productsDatabase.importWizard.confirmClose") }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              @click="showConfirmCloseDialog = false"
            >
              {{ $t("common.cancel") }}
            </v-btn>
            <v-btn
              color="error"
              text
              @click="closeWizard"
            >
              {{ $t("common.yes") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-card>
</template>

<script>
import ProductUpload from "./ProductUpload.vue";
import CoverArtUpload from "./CoverArtUpload.vue";
import ProductDetails from "./ProductDetails.vue";
import PublicationDetails from "./PublicationDetails.vue";
import Price from "./Price.vue";
import LocalBookReleaseScheduler from "@/components/LocalBookReleaseScheduler.vue";
import ProductView from "../ProductView.vue";
import StepperNavigation from "@/components/StepperNavigation.vue";
import PublishButton from "@/components/PublishButton.vue";
import LocalBookQueries from '@/graphql/LocalBookQueries';
import IRIS, { createIri } from '@/iris';
import ProductInfo from './ProductInfo.js';
import { RELEASE_TYPES } from '@/utils/Const';

// Step constants
const STEPS = {
  UPLOAD: 1,
  COVER_ART: 2,
  PRODUCT_DETAILS: 3,
  PUBLICATION_DETAILS: 4,
  PRICE: 5,
  REVIEW: 6,
  RELEASE: 7,
  SUCCESS: 8
};

const STEP_NAMES = ["upload", "coverArt", "productDetails", "publicationDetails", "price", "review", "release", "success"];

export default {
  name: "ProductImportWizard",
  components: {
    ProductUpload,
    CoverArtUpload,
    ProductDetails,
    PublicationDetails,
    Price,
    LocalBookReleaseScheduler,
    ProductView,
    StepperNavigation,
    PublishButton
  },
  props: {
    editProduct: {
      type: Object,
      default: null
    }
  },
  emits: ["close", "product-created"],
  data() {
    return {
      currentStepIndex: STEPS.UPLOAD,
      steps: STEP_NAMES,
      productInfoManager: null,
      isLoading: true,
      showConfirmCloseDialog: false,
      currentReleaseType: RELEASE_TYPES.SCHEDULED,
      isPublishing: false,
      stepValidations: {
        [STEPS.UPLOAD]: false,
        [STEPS.COVER_ART]: false,
        [STEPS.PRODUCT_DETAILS]: false,
        [STEPS.PUBLICATION_DETAILS]: false,
        [STEPS.PRICE]: false,
        [STEPS.REVIEW]: true,
        [STEPS.RELEASE]: false
      }
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.currentStepIndex - 1];
    },
    stepperSteps() {
      return this.steps.map((step) => ({
        title: this.$t(
          `admin.productsDatabase.importWizard.steps.${step}.shortTitle`
        ),
      }));
    },
    bookId() {
      return this.productInfoManager?.getBookId();
    },
    productInfo() {
      // Backward compatibility - return the raw data
      return this.productInfoManager?.getData();
    },
    coverArtUrl() {
      const book = this.productInfoManager?.getBook();
      return book?.imageUrl || book?.thumbnailUrl;
    },
    // Computed property aliases for backward compatibility
    isUploadComplete() {
      return this.productInfoManager?.isUploadComplete() || false;
    },
    isCoverArtComplete() {
      const hasExistingImage = this.coverArtUrl;
      if (this.editProduct && hasExistingImage) {
        return true;
      }
      return this.hasUploadedNewCoverArt;
    },
    isProductDetailsComplete() {
      return this.productInfoManager?.isProductDetailsComplete() || false;
    },
    isPublicationDetailsComplete() {
      return this.productInfoManager?.isPublicationDetailsComplete() || false;
    },
    isPriceReady() {
      return this.productInfoManager?.isPriceReady() || false;
    },
    isReviewReady() {
      return this.productInfoManager?.isReviewReady() || false;
    },
    isReleaseScheduleReady() {
      return this.releaseScheduleValid;
    }
  },
  async created() {
    this.STEPS = STEPS;
    
    this.productInfoManager = new ProductInfo(null, this.$apollo);
    
    
    try {
      if (this.editProduct && this.editProduct.id) {
        // Load existing product data if editing
        const iri = createIri(IRIS.localBook, this.editProduct.id);
        await this.productInfoManager.loadProduct(iri, LocalBookQueries);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      this.$store.dispatch('snackbar/showSnackbar', {
        text: this.$t('admin.productsDatabase.importWizard.loadError'),
        color: 'error'
      });
    } finally {
      this.isLoading = false;
    }
  },
  beforeDestroy() {
    if (this.productInfoManager) {
      this.productInfoManager.destroy();
    }
  },
  methods: {
    isStepComplete(step) {
      for (let i = 1; i <= step; i++) {
        if (!this.stepValidations[i]) {
          return false;
        }
      }
      return true;
    },

    handleUploadError(errorInfo) {
      console.error("Upload error:", errorInfo);
    },
    
    handleStepValidation(step, isValid) {
      this.$set(this.stepValidations, step, isValid);
    },

    handleReleaseTypeChange(releaseType) {
      this.currentReleaseType = releaseType;
    },

    async confirmAndPublish() {
      try {
        this.isPublishing = true;
        
        // Check if there are any unsaved changes to publish
        if (this.productInfoManager?.unsavedFields?.size > 0) {
          // Use productInfoManager to save all unsaved fields
          await this.productInfoManager.publishProduct();
          
          // Handle UI flow
          this.currentStepIndex = STEPS.SUCCESS;
        } else {
          // No changes to publish, just finish the wizard
          this.finishWizard();
        }
      } catch (error) {
        console.error('Error publishing book:', error);
        this.$store.dispatch('snackbar/showSnackbar', {
          text: 'Error publishing book. Please try again.',
          color: 'error'
        });
      } finally {
        this.isPublishing = false;
      }
    },

    finishWizard() {
      const productData = this.productInfoManager?.getData();
      if (productData) {
        this.$emit("product-created", productData);
      }
      this.$emit("close");
      this.resetWizard();
    },

    resetWizard() {
      if (this.productInfoManager) {
        this.productInfoManager.destroy();
      }
      
      this.currentStepIndex = STEPS.UPLOAD;
      this.productInfoManager = new ProductInfo(null, this.$apollo);
      this.currentReleaseType = RELEASE_TYPES.SCHEDULED;
      this.isPublishing = false;
      this.hasUploadedNewCoverArt = false;
      this.releaseScheduleValid = false;
    },

    confirmCloseWizard() {
      this.showConfirmCloseDialog = true;
    },

    closeWizard() {
      this.showConfirmCloseDialog = false;
      this.resetWizard();
      this.$emit("close");
    },

    canNavigateToStep(stepIndex) {
      // Allow navigation if it's the current step or a previous step
      if (stepIndex <= this.currentStepIndex) {
        return true;
      }
      // Check if all prerequisite steps are complete
      if (stepIndex === STEPS.UPLOAD) return true;
      return this.isStepComplete(stepIndex - 1);
    },

    navigateToStep(stepIndex) {
      // Only navigate if allowed
      if (this.canNavigateToStep(stepIndex)) {
        this.currentStepIndex = stepIndex;
      }
      // Otherwise, the click does nothing (or you could show a message)
    },

  },
};
</script>

<style scoped>
.import-wizard {
  background-color: var(--v-backgroundSecondary-base);
}

.monospace-font {
  font-family: monospace;
  color: var(--v-primaryText-base);
  background-color: var(--v-lightestAccent-base);
}

/* Style for clickable/editable steps */
::v-deep .v-stepper__header .v-stepper__step.step-editable {
  cursor: pointer;
}

/* Optional: Style for non-editable steps to make them look disabled */
::v-deep .v-stepper__header .v-stepper__step:not(.step-editable):not(.v-stepper__step--active):not(.v-stepper__step--complete) {
  cursor: not-allowed;
  opacity: 0.6; /* Make non-clickable steps appear faded */
}

/* Ensure active/complete steps retain their default Vuetify styling */
::v-deep .v-stepper__header .v-stepper__step--active .v-stepper__label,
::v-deep .v-stepper__header .v-stepper__step--complete .v-stepper__label {
   color: inherit; /* Or specific color if needed */
}
::v-deep .v-stepper__header .v-stepper__step.step-editable:not(.v-stepper__step--active):not(.v-stepper__step--complete) .v-stepper__label {
  color: var(--v-primary-base); /* Make editable step text primary color */
}

</style>
